import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import servicesApi from '../../../api/servicesApi';
import serviceOrdersApi from '../../../api/serviceOrdersApi';
import { applyServerErrors, getErrorMessage } from '../../../utils/errorHandler';

const NEW_TYPE_OPTION = '__new__';

const schema = yup.object({
  name: yup.string().trim().required('El nombre es obligatorio').max(255),
  description: yup.string().nullable(),
  price: yup
    .number()
    .typeError('Ingresa un precio válido')
    .min(0, 'Debe ser mayor o igual a 0')
    .required('El precio es obligatorio'),
  service_type_id: yup
    .number()
    .typeError('Selecciona un tipo de servicio')
    .required('Selecciona un tipo de servicio'),
  is_active: yup.boolean().default(true),
});

const emptyValues = {
  name: '',
  description: '',
  price: '',
  service_type_id: '',
  is_active: true,
};

// Formulario de alta/edicion de un servicio del catalogo. `service` nulo significa "crear nuevo".
export default function ServiceFormDialog({ open, onClose, onSaved, service, serviceTypes, onServiceTypeCreated }) {
  const { enqueueSnackbar } = useSnackbar();
  const isEditing = Boolean(service);

  const [types, setTypes] = useState(serviceTypes);
  const [newTypeOpen, setNewTypeOpen] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeError, setNewTypeError] = useState('');
  const [creatingType, setCreatingType] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: emptyValues,
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    setTypes(serviceTypes);
    setNewTypeOpen(false);
    setNewTypeName('');
    setNewTypeError('');

    reset(
      service
        ? {
            name: service.name,
            description: service.description || '',
            price: service.price,
            service_type_id: service.service_type_id,
            is_active: service.is_active,
          }
        : emptyValues
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, service, reset]);

  const handleCreateType = async () => {
    const name = newTypeName.trim();
    if (!name) {
      setNewTypeError('Ingresa un nombre para el tipo de servicio.');
      return;
    }

    setCreatingType(true);
    setNewTypeError('');
    try {
      const { data } = await serviceOrdersApi.createServiceType({ name });
      const created = data.data;
      setTypes((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      onServiceTypeCreated?.(created);
      setValue('service_type_id', created.id, { shouldValidate: true });
      setNewTypeOpen(false);
      setNewTypeName('');
      enqueueSnackbar('Tipo de servicio creado correctamente.', { variant: 'success' });
    } catch (error) {
      setNewTypeError(getErrorMessage(error, 'No se pudo crear el tipo de servicio.'));
    } finally {
      setCreatingType(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      if (isEditing) {
        await servicesApi.updateService(service.id, data);
        enqueueSnackbar('Servicio actualizado correctamente.', { variant: 'success' });
      } else {
        await servicesApi.createService(data);
        enqueueSnackbar('Servicio creado correctamente.', { variant: 'success' });
      }
      onSaved();
    } catch (error) {
      const handled = applyServerErrors(error, setError);
      if (!handled) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudo guardar el servicio.'), { variant: 'error' });
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Editar servicio' : 'Nuevo servicio'}</DialogTitle>

      <DialogContent dividers>
        <Stack component="form" id="service-form" spacing={2.5} onSubmit={handleSubmit(onSubmit)} noValidate>
          {errors.root?.message && <Alert severity="error">{errors.root.message}</Alert>}

          <TextField
            label="Nombre del servicio"
            fullWidth
            error={Boolean(errors.name)}
            helperText={errors.name?.message}
            {...register('name')}
          />

          <TextField
            label="Descripción"
            fullWidth
            multiline
            minRows={2}
            error={Boolean(errors.description)}
            helperText={errors.description?.message}
            {...register('description')}
          />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Precio (S/)"
                type="number"
                fullWidth
                slotProps={{ htmlInput: { step: '0.01', min: 0 } }}
                error={Boolean(errors.price)}
                helperText={errors.price?.message}
                {...register('price')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="service_type_id"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Tipo de servicio"
                    fullWidth
                    error={Boolean(errors.service_type_id)}
                    helperText={errors.service_type_id?.message}
                    onChange={(e) => {
                      if (e.target.value === NEW_TYPE_OPTION) {
                        setNewTypeOpen(true);
                        return;
                      }
                      field.onChange(e.target.value);
                    }}
                  >
                    {types.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                    <Divider />
                    <MenuItem value={NEW_TYPE_OPTION} sx={{ color: 'primary.main' }}>
                      <AddIcon fontSize="small" sx={{ mr: 1 }} />
                      Agregar nuevo tipo de servicio
                    </MenuItem>
                  </TextField>
                )}
              />
            </Grid>
          </Grid>

          {newTypeOpen && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ alignItems: { sm: 'flex-start' }, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}
            >
              <TextField
                label="Nombre del nuevo tipo"
                fullWidth
                autoFocus
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                error={Boolean(newTypeError)}
                helperText={newTypeError}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateType();
                  }
                }}
              />
              <Stack direction="row" spacing={1} sx={{ pt: { sm: 1 } }}>
                <Button
                  variant="contained"
                  onClick={handleCreateType}
                  disabled={creatingType}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {creatingType ? 'Agregando...' : 'Agregar'}
                </Button>
                <Button
                  onClick={() => {
                    setNewTypeOpen(false);
                    setNewTypeName('');
                    setNewTypeError('');
                  }}
                  disabled={creatingType}
                >
                  Cancelar
                </Button>
              </Stack>
            </Stack>
          )}

          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                label="Servicio activo (visible para el cliente)"
              />
            )}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button type="submit" form="service-form" variant="contained" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ServiceFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  service: PropTypes.object,
  serviceTypes: PropTypes.array.isRequired,
  onServiceTypeCreated: PropTypes.func,
};

ServiceFormDialog.defaultProps = {
  service: null,
  onServiceTypeCreated: undefined,
};
